import { useState, useEffect, useCallback } from 'react';
import {
  getOrderWithRelation,
  getPaymentHistory,
  getOrderStateHistory,
  getOrderReferences,
} from '@/api/order-api';
import {
  OrderResponse,
  OrderItemResponse,
  OrderReferenceItem,
  OrderStateHistory,
  PaymentRecord,
} from '@/types/order-type';
import { Fulfillment } from '@/types/fulfillment-type';
import { toast } from '@/hooks/use-toast';
import { Customer } from '@/types/customer-type';

interface UseOrderDetailsDataReturn {
  order: OrderResponse | null;
  customer: Customer | null;
  orderItems: OrderItemResponse[];
  fulfillment: Fulfillment | null;
  payments: PaymentRecord[];
  orderReferences: OrderReferenceItem[];
  orderStateHistory: OrderStateHistory[];
  isLoading: boolean;
  error: Error | string | null;
  refresh: () => void;
}

export function useOrderDetailsData(orderId: string | null): UseOrderDetailsDataReturn {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItemResponse[]>([]);
  const [fulfillment, setFulfillment] = useState<Fulfillment | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | string | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [orderReferences, setOrderReferences] = useState<OrderReferenceItem[]>([]);
  const [orderStateHistory, setOrderStateHistory] = useState<OrderStateHistory[]>([]);

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;

    setIsLoading(true);
    try {
      const orderWithRelation = await getOrderWithRelation(orderId);
      if (orderWithRelation.success) {
        setOrder(orderWithRelation.data.order);
        setCustomer(orderWithRelation.data.customer);
        setOrderItems(orderWithRelation.data.orderItems);
        setFulfillment(orderWithRelation.data.fulfillment || null);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error('Error fetching order:', err.message);
        setError(err);
      } else {
        console.error('Unknown error:', err);
        setError(new Error('An unexpected error occurred'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  const fetchPaymentHistory = useCallback(async () => {
    if (!orderId) return;

    try {
      const resp = await getPaymentHistory(orderId);
      if (resp.success) {
        setPayments(resp.data);
      } else {
        throw new Error(resp.message || 'Error fetching payment history');
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error('Error fetching payment history:', err.message);
        setError(err);
      } else {
        console.error('Unknown error:', err);
        setError(new Error('An unexpected error occurred'));
      }
    }
  }, [orderId]);

  const fetchOrderHistory = useCallback(async () => {
    if (!orderId) return;

    try {
      const resp = await getOrderStateHistory(orderId);
      if (resp.success) {
        setOrderStateHistory(resp.data);
      } else {
        throw new Error(resp.message || 'Error fetching order history');
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error('Error fetching order history:', err.message);
        setError(err);
      } else {
        console.error('Unknown error:', err);
        setError(new Error('An unexpected error occurred'));
      }
    }
  }, [orderId]);

  const fetchOrderReferences = useCallback(async () => {
    if (!orderId) return;

    try {
      const resp = await getOrderReferences(orderId);
      if (resp.success) {
        setOrderReferences(resp.data);
      } else {
        toast({
          title: 'Error',
          description: resp.message || 'Fail to fetch order references.',
          variant: 'destructive',
        });
      }
    } catch (err: unknown) {
      console.error('Error fetching order references:', err);
      toast({
        title: 'Error',
        description: 'Fail to fetch order references.',
        variant: 'destructive',
      });
    }
  }, [orderId]);

  const refresh = useCallback(() => {
    fetchOrder();
    fetchPaymentHistory();
    fetchOrderHistory();
    fetchOrderReferences();
  }, [fetchOrder, fetchPaymentHistory, fetchOrderHistory, fetchOrderReferences]);

  useEffect(() => {
    if (!orderId) return;
    refresh();
  }, [orderId, refresh]);

  return {
    order,
    customer,
    orderItems,
    fulfillment,
    payments,
    orderReferences,
    orderStateHistory,
    isLoading,
    error,
    refresh,
  };
}
